import { CardContent, CardHeader, Stack } from "@mui/material";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import MaterialTypography from "../../../components/MaterialTypography";
import { useNavigate } from "react-router-dom";
import Table from "../../../components/Table";
import { useEffect, useState } from "react";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { deleteData, fetchData, postData } from "../../../services";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/index";
import { useLoading } from "../../../components/Loading";
import { useSnackbar } from "../../../context/popup";
import UploadFile from "../../Utils/UploadFile";
import InsurerFinancerFilterDialog from './components/InsurerFinancerFilterDialog.js';

const InsurerFinancerList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector(
    (state) => state?.financerStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.financerStore?.rowsPerPage
  );
  const totalPages = useSelector(
    (state) => state?.financerStore?.totalPages
  );
  
  const [isRefresh, setIsRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [financerData, setFinancerData] = useState([]);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
      isActive: "",
      gInsurerId: "",
      gFinancerId: ""
  });

  useEffect(() => {
    fetchFinancerList();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //List API
  const fetchFinancerList = async () => {
    setCircleLoading(true);
    let apiUrl = `/reference/insurer/financer?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}&gFinancerId=${filterData?.gFinancerId}`;
    try {
      //for active and inactive status
      if(filterData.isActive !== "") {
        apiUrl += `&isActive=${filterData.isActive}`;
      }
      if(filterData.gInsurerId !== "") {
        apiUrl += `&gInsurerId=${filterData.gInsurerId}`;
      } 
      const response = await fetchData(apiUrl);
      if (response?.success) {
        setFinancerData(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message, "error");
    } finally {
      setCircleLoading(false);
    }
  };

  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true);
    setUploadSuccess(false);

    try {
      const formData = new FormData();

      formData.append("file", file);

      const result = await postData(`/reference/motor/insurer/bulkUpload/insurer-financer`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (result?.success || (result && result.length > 0)) {
        setUploadSuccess(true);
        setOpenUploadFileDialog(false);
        setIsRefresh(!isRefresh);
        showSnackbar(result?.message || "File uploaded successfully", "success");
      } else {
        setUploadSuccess(false);
        showSnackbar(result?.message || "Failed to upload file", "error");
      }
    } catch (error) {
      setOpenUploadFileDialog(false);
      const err = error?.response?.data;
      showSnackbar(err?.message || "Unknown error occurred", "error");
      console.error("Upload error:", err || error);
    } finally {
      setFileUploading(false);
    }
  };

  const handleDelete = async (row) => {
    setCircleLoading(true);
    try {
      const response = await deleteData(`/reference/insurer/financer/delete`, {
        ids: [row?.id],
      });
      const responseMessage = response?.message;
      setIsRefresh(!isRefresh);
      if (response?.success) {
        showSnackbar(responseMessage, "success");
      } else {
        showSnackbar(responseMessage, "error");
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message, "error");
    } finally {
      setCircleLoading(false);
    }
  };

  //Table Mapping
  const financerListTable = {
    columns: [
      {
        key: "sno",
        label: "S.No.",
        renderCell: (row) => row.sno,
      },
      {
        key: "gInsurerName",
        label: "General Insurer",
        renderCell: (row) => row.gInsurerName,
      },
      {
        key: "gFinancerName",
        label: "General Financer",
        renderCell: (row) => row.gFinancerName,
      },
      {
        key: "code",
        label: "Code",
        renderCell: (row) => row.code,
      },
      {
        key: "name",
        label: "Name",
        renderCell: (row) => row.name,
      },
      {
        key: "isActive",
        label: "Status",
        renderCell: (row) => (
          <span>
            {row.isActive ? (
              <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
            ) : (
              <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
            )}
          </span>
        ),
      },
    ],
    rows: financerData?.map((financer, index) => ({
      id: financer?.id,
      sno: index + 1,
      gInsurerName: financer?.gInsurerName || "",
      gFinancerName: financer?.gFinancerName || "",
      code: financer?.code || "",
      name: financer?.name || "",
      isActive: financer?.isActive,
    })),
  };

  const handleAddNewFinancer = () => {
    navigate(`/insurer-master/insurer-financer/add-financer`);
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,

    onEdit: (row) => {
      navigate(
        `/insurer-master/insurer-financer/add-financer?pageType=edit&id=${row.id}`
      );
    },
    onView: (row) => {
      navigate(
        `/insurer-master/insurer-financer/add-financer?pageType=view&id=${row.id}`
      );
    },
    onDelete: (row) => handleDelete(row),
  };

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleBulkUpload = () => {
    setOpenUploadFileDialog(true);
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Financer List
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of Financer around the world.
              </MaterialTypography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <MaterialButton
              variant="gradient"
              color="dark"
              size="small"
              startIcon={<FilterAlt/>}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              >
              Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => handleBulkUpload()}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewFinancer()}
              >
                Financer
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <CardContent>
        <Table
          columns={financerListTable?.columns}
          data={financerListTable?.rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel="No Insurer Financer found"
          handleSearch={handleSearch}
        />
      </CardContent>
      <UploadFile
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        onUploadSuccess={handleFileUploadSuccess}
        fileTypes={["csv", "xlsx"]}
        fileUploading={fileUploading}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setFileUploading={setFileUploading}
      />
      <InsurerFinancerFilterDialog
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
            setAnchorEl(null);   // close popup
            fetchFinancerList();  // call API after filter applied
        }}
        />
    </DashboardLayout>
  );
};

export default InsurerFinancerList;